/**
 * Dictionnaire français — **source de vérité** des clés de traduction (PRD NFR-23).
 *
 * La traduction est incrémentale : la coquille, l'Overview et le Deploy sont couverts ; les autres
 * pages sont converties au fil des passes suivantes. Une clé absente retombe sur la clé elle-même.
 */
export const fr = {
  'common.loading': 'Chargement…',
  'common.error': 'Erreur',

  'nav.open': 'Ouvrir la navigation',
  'nav.close': 'Fermer la navigation',
  'nav.expandSection': 'Déplier la section',
  'nav.collapseSection': 'Replier la section',

  'search.label': 'Recherche',
  'search.placeholder': 'Rechercher projets, vidéos, modèles…',
  'search.clear': 'Effacer la recherche',
  'search.searching': 'Recherche…',
  'search.empty': 'Aucun résultat.',
  'search.group.videos': 'Vidéos',
  'search.group.projects': 'Projets',
  'search.group.missions': 'Sessions de vol',
  'search.group.models': 'Modèles',
  'search.group.libraryModels': 'Bibliothèque de modèles',

  'assistant.title': 'Assistant IA',
  'assistant.close': 'Fermer l’assistant',
  'assistant.intro': 'Posez une question sur vos projets, vidéos ou modèles.',
  'assistant.placeholder': 'Votre question…',
  'assistant.disabledPlaceholder': 'Assistant indisponible',
  'assistant.inputLabel': 'Question à l’assistant',
  'assistant.send': 'Envoyer',
  'assistant.thinking': 'L’assistant réfléchit…',
  'assistant.attach': 'Joindre une vidéo',
  'assistant.attaching': 'Téléversement…',
  'assistant.attachRemove': 'Retirer la pièce jointe',
  'assistant.dropHint': 'Déposez une vidéo pour la joindre',
  'assistant.adopt': 'Adopter',
  'assistant.adopting': 'Adoption…',
  'assistant.adopted': 'Adopté',
  'assistant.adoptFailed': 'Échec de l’adoption',
  'assistant.adoptNeedsEditor': 'Rôle éditeur requis pour adopter.',
  'assistant.soonTitle': 'À venir',
  'assistant.soonBody':
    'L’assistant sera disponible une fois la clé API configurée côté serveur (ADR-0016).',

  'account.assistant': 'Assistant IA',
  'account.notifications': 'Notifications (à venir)',
  'account.profile': 'Profil',
  'account.settings': 'Réglages',
  'account.logout': 'Se déconnecter',
  'account.themeToLight': 'Passer en thème jour',
  'account.themeToDark': 'Passer en thème nuit',
  'language.label': 'Langue',
  'language.french': 'Français',
  'language.english': 'Anglais',
  'language.ukrainian': 'Ukrainien',

  'overview.title': 'Overview',
  'overview.intro':
    'Vue d’ensemble de votre organisation et accès aux quatre grandes fonctionnalités.',
  'overview.tile.projects': 'Projets',
  'overview.tile.campaigns': 'Campagnes',
  'overview.tile.sessions': 'Sessions de vol',
  'overview.tile.videos': 'Vidéos',
  'overview.tile.images': 'Images',
  'overview.tile.models': 'Modèles',
  'overview.projects.title': 'Projets',
  'overview.projects.new': 'Nouveau projet',
  'overview.projects.manage': 'Gérer dans Data →',
  'overview.projects.empty':
    'Aucun projet. Regroupez des vidéos, images et modèles dans un projet.',
  'overview.projects.videos': 'vidéos',
  'overview.projects.rename': 'Renommer',
  'overview.projects.delete': 'Supprimer',
  'overview.projects.deleteTitle': 'Supprimer le projet',
  'overview.projects.deleteBody':
    'Le projet est supprimé, mais les vidéos, images et modèles associés restent intacts.',
  'overview.projects.formName': 'Nom du projet',
  'overview.projects.formDesc': 'Description (facultative)',
  'overview.projects.namePlaceholder': 'ex. Drone suiveur',
  'overview.projects.save': 'Enregistrer',
  'overview.projects.cancel': 'Annuler',
  'overview.recentVideos': 'Dernières vidéos',
  'overview.evaluatedModels': 'Modèles évalués',
  'overview.noVideos': 'Aucune vidéo.',
  'overview.noVideosCta': 'Ajoutez-en dans Data collection.',
  'overview.noEval': 'Aucune évaluation.',
  'overview.noEvalCta': 'Évaluez un modèle dans Model.',
  'overview.seeAll': 'Tout voir',
  'overview.compare': 'Comparer',

  // Bande de guidage. Deux visages d'une même bande : le pipeline pédagogique pour une
  // organisation encore vide, la barre de commande compacte + reprise une fois qu'elle vit.
  'overview.guide.lead.new': 'Votre chaîne, du terrain au matériel.',
  'overview.guide.lead.active': 'Reprenez où vous en étiez, ou lancez une nouvelle action.',
  'overview.guide.workflow.title': 'Créer un workflow complet',
  'overview.guide.workflow.desc':
    'Enchaînez les quatre étapes en un parcours guidé, de la donnée au déploiement.',
  'overview.guide.workflow.cta': 'Démarrer',
  'overview.guide.step.data': 'Données',
  'overview.guide.step.data.desc': 'Vidéos, simulation',
  'overview.guide.step.annotation': 'Annotation',
  'overview.guide.step.annotation.desc': 'Étiqueter, pré-annoter',
  'overview.guide.step.model': 'Modèle',
  'overview.guide.step.model.desc': 'Importer, adopter, évaluer',
  'overview.guide.step.deploy': 'Déploiement',
  'overview.guide.step.deploy.desc': 'Empaqueter pour la cible',
  'overview.guide.state.todo': 'À faire',
  'overview.guide.state.soon': 'À venir',
  'overview.guide.cmd.data': 'Ajouter des données',
  'overview.guide.cmd.model': 'Créer / mettre à jour un modèle',
  'overview.guide.cmd.deploy': 'Déployer',
  'overview.guide.resume.label': 'Reprendre',
  'overview.guide.resume.toAnnotate': 'à annoter',
  'overview.guide.resume.open': 'ouvrir',

  // Parcours guidé pas-à-pas (ADR-0020). L'avancement se lit sur les données, rien à cocher.
  'workflow.title': 'Parcours guidé',
  'workflow.intro':
    'De la donnée au modèle déployé, une étape à la fois. Votre avancement se lit sur vos données — rien à cocher à la main.',
  'workflow.progress': 'étapes terminées',
  'workflow.revisit': 'Revisiter',
  'workflow.state.done': 'Fait',
  'workflow.state.current': 'À faire maintenant',
  'workflow.state.todo': 'À venir',
  'workflow.state.locked': 'Bientôt',
  'workflow.count.videos': 'vidéos',
  'workflow.count.annotated': 'annotées',
  'workflow.count.models': 'modèles',
  'workflow.count.evaluated': 'évalués',
  'workflow.allDone.title': 'Parcours complété',
  'workflow.allDone.desc':
    'Vos données ont traversé toute la chaîne disponible. Le déploiement sur matériel arrive bientôt.',
  'workflow.step.data.title': 'Ajouter des données',
  'workflow.step.data.body':
    'Déposez vos vidéos de drone et leur télémétrie. Elles sont transcodées, puis prêtes à annoter.',
  'workflow.step.data.action': 'Ajouter des données',
  'workflow.step.annotation.title': 'Annoter',
  'workflow.step.annotation.body':
    'Étiquetez les objets d’intérêt en boîtes englobantes, ou laissez la pré-annotation vous devancer.',
  'workflow.step.annotation.action': 'Annoter des vidéos',
  'workflow.step.model.title': 'Obtenir un modèle',
  'workflow.step.model.body':
    'Importez un modèle .onnx, ou adoptez-en un depuis la bibliothèque — prêt à évaluer sur vos données.',
  'workflow.step.model.action': 'Créer ou adopter un modèle',
  'workflow.step.evaluation.title': 'Évaluer',
  'workflow.step.evaluation.body':
    'Mesurez un modèle sur vos vidéos annotées (mAP, précision, rappel) pour savoir ce qu’il vaut vraiment.',
  'workflow.step.evaluation.action': 'Évaluer un modèle',
  'workflow.step.deploy.title': 'Déployer sur le matériel',
  'workflow.step.deploy.body':
    'Empaquetez un modèle validé vers le runtime cible (ONNX, TFLite, TensorRT). Bientôt disponible.',
  'workflow.step.deploy.action': 'Déployer',

  'deploy.title': 'Deploy',
  'deploy.intro':
    'Déployez un modèle validé sur le matériel de votre choix (drone, UGV…) et adaptez-le à ses contraintes.',
  'deploy.soonBadge': 'À venir (ADR-0014, Phase D).',
  'deploy.soonLead':
    'Cette section permettra de packager un modèle vers le format du runtime cible (ONNX, TFLite, TensorRT), de le quantifier au besoin, et de le déployer sur un appareil choisi dans une liste.',
  'deploy.families': 'Deux familles de caractéristiques, distinctes par conception :',
  'deploy.computeLabel': 'Calcul et capteur',
  'deploy.computeText':
    'SoC/accélérateur, latence, puissance, résolution/champ de la caméra. C’est ce qui adapte réellement le modèle : choix de sa taille et de sa quantification pour tenir le budget embarqué.',
  'deploy.airframeLabel': 'Airframe',
  'deploy.airframeText':
    'poids, dynamique de vol, moteurs. Conservé comme métadonnée de flotte (inventaire, endurance, planification) ; ces grandeurs ne paramètrent pas le modèle de vision.',

  'model.error.load': 'Chargement impossible',
  'model.error.historyUnavailable': 'Historique indisponible',
  'model.error.evaluate': 'Évaluation impossible',
  'model.error.required': 'Nom, version et fichier .onnx sont requis.',
  'model.error.tooLarge': 'Fichier trop volumineux (2 Go maximum).',
  'model.error.uploadRefused': 'Dépôt de l’artefact refusé par le stockage',
  'model.error.import': 'Import impossible',
  'model.error.delete': 'Suppression impossible',
  'model.loading': 'Chargement…',
  'model.title': 'Modèles',
  'model.intro':
    'Importez vos modèles de détection (ONNX) pour les évaluer sur vos données annotées et, à terme, les entraîner et les déployer sur votre matériel (ADR-0014).',
  'model.notif.errorTitle': 'Erreur',
  'model.notif.actionErrorTitle': 'Action impossible',
  'model.import.title': 'Construire un modèle',
  'model.field.name': 'Nom',
  'model.field.namePlaceholder': 'ex. yolov8n',
  'model.field.version': 'Version',
  'model.field.versionPlaceholder': 'ex. 1',
  'model.field.inputSize': 'Taille d’entrée',
  'model.field.classes': 'Classes (une par ligne, dans l’ordre des indices du modèle)',
  'model.field.classesHelper': 'Vide = classes COCO par défaut d’un YOLO générique.',
  'model.field.description': 'Description (optionnelle)',
  'model.field.artifact': 'Artefact .onnx',
  'model.import.busy': 'Import…',
  'model.import.submit': 'Importer le modèle',
  'model.compare.title': 'Comparaison',
  'model.compare.evaluatedSuffix': 'évalués',
  'model.compare.col.model': 'Modèle',
  'model.compare.col.precision': 'Précision',
  'model.compare.col.recall': 'Rappel',
  'model.compare.col.images': 'Images',
  'model.compare.hint':
    'Classé par mAP décroissant. La comparaison n’est valable que si les modèles ont été évalués sur le même jeu — vérifiez la vidéo dans l’historique de chacun.',
  'model.registry.title': 'Registre',
  'model.zoo.mine': 'Vos modèles',
  'model.zoo.catalog': 'Catalogue pré-entraîné',
  'model.zoo.catalogHint': 'Modèles déjà entraînés (Yolo, ResNet…), prêts à adopter.',
  'model.registry.empty': 'Aucun modèle enregistré pour l’instant.',
  'model.status.ready': 'Prêt',
  'model.status.pending': 'En attente',
  'model.status.failed': 'Échec',
  'model.classesCountSuffix': 'classe(s)',
  'model.source.trained': 'entraîné',
  'model.source.imported': 'importé',
  'model.byPrefix': 'par',
  'model.action.evaluate': 'Évaluer',
  'model.action.hide': 'Masquer',
  'model.action.history': 'Historique',
  'model.action.delete': 'Supprimer',
  'model.eval.headingPrefix': 'Évaluer',
  'model.eval.busy': 'Lancement…',
  'model.eval.submit': 'Lancer l’évaluation',
  'model.eval.cancel': 'Annuler',
  'model.eval.intro':
    'L’évaluation compare les prédictions du modèle aux annotations validées de la vidéo, sur le worker. Les métriques (mAP, précision, rappel) s’afficheront sur le modèle une fois la course terminée.',
  'model.eval.videoLabel': 'Vidéo (jeu de validation)',
  'model.history.loading': 'Chargement de l’historique…',
  'model.history.empty': 'Aucune évaluation pour ce modèle.',
  'model.history.status.pending': 'en attente',
  'model.history.status.running': 'en cours',
  'model.history.status.done': 'terminée',
  'model.history.status.failed': 'échec',
  'model.history.videoFallback': 'vidéo',
  'model.history.col.class': 'Classe',
  'model.history.col.gt': 'VT',
  'model.history.col.confusion': 'VP/FP/FN',

  // Navigation par sections de la page Model (ADR-0017).
  'model.section.modelZoo': 'Model Zoo',
  'model.section.build': 'Construire un modèle',
  'model.section.objectives': 'Objectifs',
  'model.objectives.hint':
    'Chaque projet et son objectif : l’avancement se lit sur vos données. Ouvrez un projet pour définir ou modifier son objectif.',
  'model.objectives.empty':
    'Aucun projet pour l’instant. Créez-en un depuis l’Overview, puis définissez son objectif.',
  'model.objectives.none': 'Aucun objectif — ouvrez le projet pour en définir un.',
  'model.objectives.resume': 'Reprise',
  'model.objectives.completed': 'Complété',
  'model.objectives.ongoing': 'Objectifs en cours',
  'model.section.useCases': 'Cas d’usages',

  // Bloc processus « Créer un modèle » en tête de page (ADR-0028).
  'model.process.title': 'Créer un modèle',
  'model.process.intro':
    'De l’objectif au modèle déployé, en six étapes. Tout part d’un objectif projet : ce que vous voulez détecter, sur quelles données, pour quel matériel.',
  'model.process.flowLabel': 'Étapes de création d’un modèle',
  'model.process.step.objective': 'Objectif',
  'model.process.step.data': 'Données',
  'model.process.step.baseModel': 'Modèle de base',
  'model.process.step.train': 'Entraîner',
  'model.process.step.evaluate': 'Évaluer',
  'model.process.step.deploy': 'Déployer',
  'model.process.cta': 'Démarrer un objectif projet',
  'model.process.ctaNote': 'On part de l’étape 1 : définir l’objectif d’un projet.',

  // Modale « Démarrer un objectif projet ».
  'model.startObjective.title': 'Démarrer un objectif projet',
  'model.startObjective.intro':
    'L’objectif se définit sur un projet. Choisissez un projet existant ou créez-en un — vous définirez ensuite ce que le modèle doit détecter.',
  'model.startObjective.projectLegend': 'Projet',
  'model.startObjective.modeExisting': 'Un projet existant',
  'model.startObjective.modeNew': 'Un nouveau projet',
  'model.startObjective.pickProject': 'Projet',
  'model.startObjective.newName': 'Nom du projet',
  'model.startObjective.confirm': 'Continuer',
  'model.startObjective.noRightTitle': 'Aucun projet accessible',
  'model.startObjective.noRightBody':
    'Il n’existe pas encore de projet et votre rôle ne permet pas d’en créer. Demandez à un éditeur de l’organisation d’en créer un.',
  'model.startObjective.useCasesTitle': 'Cas d’usage — pour cadrer l’objectif',
  'model.startObjective.useCasesHint':
    'Situations opérationnelles courantes, pour vous inspirer avant de définir l’objectif.',

  // Section « Vos modèles » — état vide.
  'model.yours.empty.title': 'Aucun modèle pour l’instant',
  'model.yours.empty.body':
    'Importez un modèle de détection (ONNX) pour l’évaluer sur vos données annotées.',
  'model.yours.empty.cta': 'Créer un modèle',

  // Section « Cas d’usages ».
  'model.useCases.intro':
    'Les situations opérationnelles qui appellent un modèle. Partez du catalogue, ou ajoutez les vôtres.',
  'model.useCases.dataLabel': 'Données',
  'model.useCases.add': 'Proposer un cas d’usage',
  'model.useCases.addSoon':
    'Bientôt : ajoutez vos propres cas d’usage, après vérification qu’ils n’existent pas déjà.',
  'model.useCase.fpv.name': 'Détection de FPV',
  'model.useCase.fpv.desc':
    'Repérer les drones FPV (pilotage immersif), souvent rapides et de petite taille.',
  'model.useCase.shahed.name': 'Détection de Shahed',
  'model.useCase.shahed.desc':
    'Identifier à distance les drones de type Shahed-136 (munitions rôdeuses).',
  'model.useCase.lightVehicle.name': 'Détection de véhicule léger',
  'model.useCase.lightVehicle.desc':
    'Repérer voitures, pick-ups et utilitaires depuis une vue aérienne.',
  'model.useCase.objectTracking.name': 'Tracking d’objet',
  'model.useCase.objectTracking.desc': 'Suivre une cible image après image une fois détectée.',
  'model.useCase.terrain.name': 'Reconnaissance de terrain',
  'model.useCase.terrain.desc':
    'Caractériser le sol : végétation, bâti, réseaux, points de passage.',
  'model.useCase.personnel.name': 'Détection de personnel',
  'model.useCase.personnel.desc':
    'Repérer des personnes en zone ouverte, de jour comme de nuit (IR).',

  // Section « Modèles » — bibliothèque pré-entraînée.
  'model.lib.intro':
    'Une bibliothèque de modèles pré-entraînés, à récupérer sur HuggingFace. Rangés par type de données, taille et objets détectés.',
  'model.lib.filter.all': 'Tous',
  'model.lib.objectsLabel': 'Objets',
  'model.lib.view': 'Voir sur HuggingFace',
  'model.lib.searchHint':
    'Interrogez cette bibliothèque depuis la barre de recherche ou l’assistant (à venir).',
  'model.lib.empty': 'Aucun modèle pour ce type de données.',
  'model.dataType.optical': 'Optique',
  'model.dataType.ir': 'IR',
  'model.dataType.acoustic': 'Acoustique',
  'model.dataType.radar': 'Radar',
  'model.lib.objects.coco': '80 classes COCO (personnes, véhicules, animaux…)',
  'model.lib.objects.aerial': 'Objets vus du ciel : piétons, voitures, camions, bus, vélos.',
  'model.lib.objects.thermalPerson': 'Personnes et véhicules en imagerie thermique.',
  'model.lib.objects.flir': 'Piétons, véhicules et deux-roues (thermique, ADAS).',
  'model.lib.objects.acousticDrone': 'Signature acoustique de drone (présence/absence).',
  'model.lib.objects.sarVehicle': 'Véhicules en imagerie radar à synthèse d’ouverture.',
  'model.lib.objects.microDoppler': 'Drones vs oiseaux par signature micro-Doppler.',

  // Fiche métier de la bibliothèque (ADR-0018).
  'model.lib.loadError': 'Chargement de la bibliothèque impossible.',
  'model.lib.liveOff': 'Enrichissement en direct désactivé — catalogue organisé.',
  'model.lib.detectsLabel': 'Détecte',
  'model.lib.reliability.high': 'Fiabilité élevée',
  'model.lib.reliability.medium': 'Fiabilité moyenne',
  'model.lib.reliability.low': 'Fiabilité faible',
  'model.lib.reliability.unevaluated': 'À évaluer',
  'model.lib.reliabilityHint.measured': 'Fiabilité mesurée sur vos données annotées.',
  'model.lib.reliabilityHint.unevaluated':
    'Pas encore évalué sur vos données — lancez une évaluation.',
  'model.lib.field.data': 'Données',
  'model.lib.field.form': 'Gabarit',
  'model.lib.field.speed': 'Vitesse',
  'model.lib.field.ready': 'Prêt à l’emploi',
  'model.lib.form.embedded': 'Léger, embarquable',
  'model.lib.form.ground': 'Station au sol',
  'model.lib.speed.realtime': 'Temps réel',
  'model.lib.speed.batch': 'Temps différé',
  'model.lib.ready.yes': 'ONNX dispo',
  'model.lib.ready.no': 'Conversion nécessaire',
  'model.lib.rightsLabel': 'Droits',
  'model.lib.rights.commercialYes': 'Usage commercial',
  'model.lib.rights.commercialEnterprise': 'Usage commercial — licence Enterprise requise',
  'model.lib.rights.commercialNo': 'Usage commercial interdit',
  'model.lib.rights.militaryYes': 'Usage militaire autorisé',
  'model.lib.rights.militaryNo': 'Usage militaire restreint',
  'model.lib.rights.copyleft': 'À contribuer en open-source (AGPL)',
  'model.lib.rights.attribution': 'Attribution requise',
  'model.lib.rights.none': 'Aucune obligation open-source',
  'model.lib.tech.title': 'Détail technique',
  'model.lib.tech.params': 'Paramètres',
  'model.lib.tech.license': 'Licence',
  'model.lib.tech.downloads': 'Téléchargements',
  'model.lib.tech.updated': 'Mis à jour',
  'model.lib.tech.reported': 'Licence déclarée',
  'model.lib.tech.source': 'Source',

  'data.status.pending': 'En attente',
  'data.status.uploading': 'Téléversement',
  'data.status.uploaded': 'Téléversée',
  'data.status.processing': 'Traitement',
  'data.status.ready': 'Prête',
  'data.status.failed': 'Échec',
  'data.title': 'Data',
  'data.subtitle': 'Vos vidéos, images et simulations — collecte et préparation des données.',
  'data.section.videos': 'Vidéos',
  'data.section.images': 'Images',
  'data.section.simulations': 'Simulations',
  'data.more.videos': 'Autres vidéos',
  'data.more.images': 'Autres images',
  'data.more.hint': 'Voir tout',
  'data.videosPage.subtitle': 'Toutes les vidéos de votre organisation.',
  'data.imagesPage.subtitle': 'Toutes les images extraites de votre organisation.',
  'data.upload.dropLabel':
    'Glissez une vidéo ici ou cliquez pour parcourir — MP4, MOV, MKV (20 Go max)',
  'data.upload.finalizing': 'Finalisation…',
  'data.upload.uploading': 'Téléversement',
  'data.action.cancel': 'Annuler',
  'data.upload.errorTitle': 'Échec du téléversement',
  'data.load.errorTitle': 'Chargement impossible',
  'data.empty': 'Aucune vidéo pour l’instant.',
  'data.emptyInSession': 'Aucune vidéo dans cette session de vol.',
  'data.meta.file': 'Fichier',
  'data.meta.size': 'Taille',
  'data.meta.added': 'Ajoutée',
  'data.meta.asset': 'Appareil',
  'data.action.moveToSession': 'Déplacer vers une session',
  'data.action.addToProject': 'Ajouter au projet',
  'data.addToProject.heading': 'Ajouter au projet',
  'data.addToProject.empty': 'Aucun projet pour l’instant — créez-en un depuis l’Overview.',
  'data.addToProject.add': 'Ajouter',
  'data.addToProject.added': 'Ajouté',
  'project.notFound.title': 'Projet introuvable',
  'project.notFound.body': 'Ce projet n’existe pas ou n’appartient pas à votre organisation.',
  'project.empty':
    'Ce projet est vide — ajoutez-y des vidéos, images ou modèles depuis « Data » et « Model ».',
  'project.videos.empty': 'Aucune vidéo dans ce projet — ajoutez-en depuis « Data ».',
  'project.videos.addedOn': 'Ajouté le',
  'project.videos.remove': 'Retirer du projet',
  'project.item.remove': 'Retirer du projet',
  'project.images.of': 'Image de',

  // Objectif de projet & plan guidé (ADR-0028).
  'objective.title': 'Objectif',
  'objective.empty':
    'Aucun objectif défini. Dites ce que vous voulez construire — la plateforme trace le plan.',
  'objective.define': 'Définir un objectif',
  'objective.edit': 'Modifier',
  'objective.remove': 'Retirer',
  'objective.plan.title': 'Plan d’actions guidé',
  'objective.form.task': 'Tâche',
  'objective.form.targets': 'Cibles à détecter',
  'objective.form.targetsHint': 'Classes visées, séparées par des virgules.',
  'objective.form.targetsPlaceholder': 'ex. Shahed-136, FPV, véhicule léger',
  'objective.form.modality': 'Nature des données',
  'objective.form.support': 'Support',
  'objective.form.deployTarget': 'Cible de déploiement',
  'objective.form.dataOnHand': 'Données déjà disponibles',
  'objective.task.DETECTION': 'Détection',
  'objective.task.TRACKING': 'Suivi',
  'objective.task.TERRAIN': 'Reconnaissance de terrain',
  'objective.task.TRAJECTORY': 'Prédiction de trajectoire',
  'objective.task.soon': 'à venir',
  'objective.modality.OPTICAL': 'Optique',
  'objective.modality.INFRARED': 'Infrarouge',
  'objective.modality.ACOUSTIC': 'Acoustique',
  'objective.modality.RADAR': 'Radar',
  'objective.modality.TELEMETRY': 'Télémétrie',
  'objective.support.IMAGE': 'Image fixe',
  'objective.support.VIDEO_STREAM': 'Flux vidéo',
  'objective.support.TIME_SERIES': 'Série temporelle',
  'objective.deploy.DRONE': 'Embarqué (drone)',
  'objective.deploy.UGV': 'Embarqué (UGV)',
  'objective.deploy.GROUND': 'Au sol',
  'objective.dataOnHand.NONE': 'Rien encore',
  'objective.dataOnHand.VIDEOS': 'Des vidéos',
  'objective.dataOnHand.LOGS': 'Des logs de vol',
  'objective.step.dataset': 'Constituer un jeu annoté',
  'objective.step.datasetUnit': 'images annotées',
  'objective.step.baseModel': 'Choisir un modèle de base',
  'objective.step.train': 'Entraîner / affiner',
  'objective.step.evaluate': 'Évaluer',
  'objective.step.deploy': 'Déployer sur un matériel',
  'objective.step.analyze': 'Analyser & améliorer',
  'objective.step.comingSoon': 'À venir',
  'objective.branch.annotate': 'Annoter des images',
  'objective.branch.reuse': 'Réutiliser des données annotées',
  'objective.branch.clean': 'Nettoyer un jeu de données',
  'objective.branch.simulate': 'Générer des données synthétiques',
  'objective.branch.field': 'Concevoir une acquisition terrain',
  'objective.branch.adopt': 'Adopter depuis la bibliothèque',
  'objective.branch.import': 'Importer un .onnx',
  'objective.branch.recommended': 'conseillé',
  'data.action.share': 'Partager',
  'data.action.delete': 'Supprimer',
  'data.sessions.title': 'Sessions de vol',
  'data.sessions.new': 'Nouvelle session',
  'data.filter.all': 'Toutes',
  'data.filter.asset': 'Appareil',
  'data.filter.allAssets': 'Tous les appareils',
  'data.sessions.edit': 'Modifier la session',
  'data.error.saveFailed': 'Enregistrement impossible.',
  'data.session.editHeading': 'Modifier la session de vol',
  'data.session.newHeading': 'Nouvelle session de vol',
  'data.action.saving': 'Enregistrement…',
  'data.action.save': 'Enregistrer',
  'data.session.hint':
    'Une session de vol regroupe les vidéos d’une sortie : une après-midi, un lieu, une campagne.',
  'data.field.name': 'Nom',
  'data.session.namePlaceholder': 'ex. Ligne HTA — secteur nord',
  'data.field.campaign': 'Campagne',
  'data.session.campaignHelper': 'La campagne (projet) qui regroupe cette session et ses images.',
  'data.field.description': 'Description',
  'data.field.location': 'Lieu',
  'data.session.locationPlaceholder': 'ex. Parcelle B12, Aubagne',
  'data.field.flightDate': 'Date du vol',
  'data.field.contributors': 'Contributeurs',
  'data.session.contributorsHelper': 'Séparés par des virgules',
  'data.session.contributorsPlaceholder': 'ex. Alice Martin, Bob Durand',
  'data.field.asset': 'Appareil',
  'data.session.assetHelper': 'Le drone ou l’UGV de la flotte qui a réalisé la sortie.',
  'data.session.assetNone': '— Aucun —',
  'data.error.title': 'Échec',
  'data.error.moveFailed': 'Déplacement impossible.',
  'data.move.heading': 'Déplacer vers une session',
  'data.move.moving': 'Déplacement…',
  'data.action.move': 'Déplacer',
  'data.move.noSessions': 'Aucune session de vol disponible. Créez-en une d’abord.',
  'data.error.deleteFailed': 'Suppression impossible.',
  'data.delete.heading': 'Supprimer la vidéo',
  'data.delete.deleting': 'Suppression…',
  'data.delete.confirmPrefix': 'La vidéo',
  'data.delete.confirmSuffix':
    'et ses fichiers seront supprimés. Les annotations existantes sont conservées à des fins de traçabilité. Cette action est irréversible.',
  'data.error.shareCreateFailed': 'Création du lien impossible.',
  'data.share.heading': 'Partager la vidéo',
  'data.share.done': 'Terminé',
  'data.share.creating': 'Création…',
  'data.share.create': 'Créer le lien',
  'data.share.introPrefix': 'Un lien public donnera accès à',
  'data.share.introSuffix': 'sans compte requis.',
  'data.share.expiryLabel': 'Expire dans (jours)',
  'data.share.includeAnnotations': 'Inclure les annotations',
  'data.share.allowDownload': 'Autoriser le téléchargement de la source',
  'data.share.created': 'Lien créé. Copiez-le : le jeton n’est affiché qu’une fois.',
  'data.share.linkLabel': 'Lien de partage',
  'data.action.copied': 'Copié',
  'data.action.copy': 'Copier',
  'data.share.validUntil': 'Valable jusqu’au',
  'data.error.exportFailed': 'Export impossible.',
  'data.images.title': 'Images',
  'data.images.hint':
    'Cliquez une image pour la sélectionner — ou maintenez et survolez pour en sélectionner plusieurs — puis exportez le lot au format YOLO (.zip).',
  'data.images.toolbarAria': 'Actions sur les images sélectionnées',
  'data.images.selectedCount': 'sélectionnée(s)',
  'data.images.noSelection': 'Aucune sélection',
  'data.images.selectAll': 'Tout sélectionner',
  'data.images.deselectAll': 'Tout désélectionner',
  'data.images.preparing': 'Préparation…',
  'data.images.download': 'Télécharger',
  'data.images.inYolo': 'en YOLO',
  'data.images.exportErrorTitle': 'Export impossible',
  'data.images.selectImageOf': 'Sélectionner l’image de',
  'data.projects.title': 'Campagnes',
  'data.projects.new': 'Nouvelle campagne',
  'data.projects.edit': 'Modifier la campagne',
  'data.project.editHeading': 'Modifier la campagne',
  'data.project.newHeading': 'Nouvelle campagne',
  'data.project.hint':
    'Une campagne (projet) regroupe des sessions de vol et leurs images — par ex. « Inspection réseau HTA 2026 ».',
  'data.project.namePlaceholder': 'ex. Inspection réseau HTA 2026',

  // Section Simulation de la page Data (ADR-0021).
  'sim.title': 'Simulation',
  'sim.lead':
    'Générez des données d’entraînement synthétiques, adaptées à la situation, quand les données réelles manquent pour affiner un modèle.',
  'sim.dormantNote':
    'Le rendu photoréaliste est une capacité opt-in : l’écriture de scénario est ouverte, le lancement s’activera avec le nœud GPU.',
  'sim.launchDormantTitle': 'Rendu indisponible',
  'sim.launchDormantBody':
    'Le scénario est enregistré. Le rendu se lancera une fois le nœud GPU activé (souveraineté opt-in).',
  'sim.startWith': 'Démarrer avec',
  'sim.describeToAssistant': 'Décrire à l’assistant',
  'sim.field.name': 'Nom du lot',
  'sim.field.namePlaceholder': 'ex. Recherche en champ — matin nuageux',
  'sim.field.environment': 'Environnement',
  'sim.field.timeOfDay': 'Moment',
  'sim.field.sky': 'Ciel',
  'sim.field.altitude': 'Altitude (m)',
  'sim.field.gimbal': 'Tangage nacelle (°)',
  'sim.field.imageCount': 'Nombre d’images',
  'sim.field.targets': 'Cibles à détecter',
  'sim.field.threats': 'Menaces aériennes',
  'sim.field.outputs': 'Sorties',
  'sim.noThreat': 'Aucune menace — ajoutez-en depuis un preset ou à la main.',
  'sim.threatPreset.fpvKamikaze': 'FPV kamikaze 10″',
  'sim.threatPreset.swarm': 'Essaim ×10',
  'sim.threatPreset.fixedWing': 'Voilure fixe',
  'sim.threatPreset.blank': 'Menace vierge',
  'sim.threat.remove': 'Retirer la menace',
  'sim.threat.type': 'Type',
  'sim.threat.label': 'Label de détection',
  'sim.threat.labelDefault': '= type',
  'sim.threat.size': 'Taille (″)',
  'sim.threat.count': 'Nombre',
  'sim.threat.speedMin': 'Vitesse min (m/s)',
  'sim.threat.speedMax': 'Vitesse max (m/s)',
  'sim.threat.approach': 'Approche',
  'sim.threat.trajectory': 'Trajectoire',
  'sim.threat.dispersion': 'Dispersion (°)',
  'sim.threatType.fpv': 'FPV',
  'sim.threatType.fixedWing': 'Voilure fixe',
  'sim.threatType.quad': 'Quad',
  'sim.threatType.vtol': 'VTOL',
  'sim.traj.dive': 'Piqué',
  'sim.traj.pass': 'Passage',
  'sim.traj.orbit': 'Orbite',
  'sim.traj.disperse': 'Dispersion',
  'sim.traj.hover': 'Stationnaire',
  'sim.approach.frontal': 'Frontale',
  'sim.approach.lateral': 'Latérale',
  'sim.approach.vertical': 'Verticale',
  'sim.approach.mixed': 'Multiples',
  'sim.field.envSource': 'Source d’environnement',
  'sim.envSource.preset': 'Preset',
  'sim.envSource.reconstruction': 'Reconstruction — depuis vos médias',
  'sim.field.referenceMedia': 'Médias de la zone',
  'sim.media.dormant': 'reconstruction 3D · dormante (GPU)',
  'sim.media.filter': 'Filtrer par nom…',
  'sim.media.all': 'Tous',
  'sim.media.videos': 'Vidéos',
  'sim.media.images': 'Images',
  'sim.media.empty': 'Aucun média — ajoutez-en via Data → Vidéos / Images.',
  'sim.media.selected': 'média(s) sélectionné(s)',
  'sim.env.field': 'Champ',
  'sim.env.forest': 'Forêt',
  'sim.env.denseHabitat': 'Habitat dense',
  'sim.env.periurban': 'Périurbain',
  'sim.time.dawn': 'Aube',
  'sim.time.day': 'Jour',
  'sim.time.dusk': 'Crépuscule',
  'sim.time.night': 'Nuit',
  'sim.sky.clear': 'Dégagé',
  'sim.sky.partlyCloudy': 'Nuageux',
  'sim.sky.overcast': 'Couvert',
  'sim.sky.fog': 'Brume',
  'sim.output.images': 'Images étiquetées',
  'sim.output.video': 'Vidéo',
  'sim.output.imagesShort': 'images',
  'sim.output.videoShort': 'vidéo',
  'sim.removeTarget': 'Retirer la cible',
  'sim.noTarget': 'Aucune cible — ajoutez-en au moins une.',
  'sim.addTarget': 'Ajouter',
  'sim.addTargetPlaceholder': 'Label (ex. personne, véhicule)',
  'sim.recap.title': 'Récapitulatif',
  'sim.recap.previewSoon': 'Aperçu 2.5D — à venir',
  'sim.recap.previewSchematic': 'Aperçu schématique 2.5D — pas le rendu final',
  'sim.recap.previewPhoto': 'Aperçu — fond représentatif, pas le rendu final',
  'sim.backdrop.label': 'Fond (base d’images)',
  'sim.backdrop.none': 'schématique (aucun choisi)',
  'sim.backdrop.choose': 'Choisir…',
  'sim.backdrop.remove': 'Retirer',
  'sim.backdrop.modalTitle': 'Choisir un fond pour l’environnement',
  'sim.backdrop.empty': 'Aucune image dans la base. Extrayez des images depuis une vidéo.',
  'sim.backdrop.close': 'Fermer',
  'sim.recap.timeSky': 'Moment · ciel',
  'sim.recap.altPitch': 'Altitude · tangage',
  'sim.create': 'Créer le lot',
  'sim.launch': 'Lancer le rendu',
  'sim.delete': 'Supprimer',
  'sim.batches': 'Lots de simulation',
  'sim.noBatch': 'Aucun lot de simulation pour le moment.',
  'sim.synthetic': 'Synthétique',
  'sim.status.draft': 'Brouillon',
  'sim.status.queued': 'En file',
  'sim.status.rendering': 'Rendu…',
  'sim.status.ready': 'À valider',
  'sim.status.approved': 'Validé',
  'sim.status.rejected': 'Rejeté',
  'sim.status.failed': 'Échec',
  'sim.cancel': 'Annuler',
  'sim.review.approve': 'Valider',
  'sim.review.reject': 'Rejeter',
  'sim.review.approveError': 'Validation impossible.',
  'sim.review.rejectError': 'Rejet impossible.',
  'sim.review.approvedBy': 'Validé par',
  'sim.review.rejectedBy': 'Rejeté par',
  'sim.review.rejectHeading': 'Rejeter ce lot ?',
  'sim.review.rejecting': 'Rejet…',
  'sim.review.reasonLabel': 'Motif du rejet',
  'sim.review.reasonPlaceholder': 'ex. rendu flou, cibles mal placées, éclairage irréaliste…',
  'sim.assistantOff': 'Assistant indisponible : à activer côté serveur (ADR-0016).',
  'sim.assistantHint':
    'Décrivez la scène ; l’assistant propose un scénario — en reprenant vos labels réels — et remplit le formulaire. Ajustez ensuite.',
  'sim.assistError': 'Proposition impossible ; reformulez votre description.',
  'sim.assistantPreamble':
    'Propose un scénario de simulation (environnement, moment, ciel, altitude, cibles) pour ce besoin :',
  'sim.assistantPlaceholder': 'ex. détecter des personnes tombées dans un champ au petit matin',
  'sim.assistantSend': 'Envoyer',
  'sim.template.fieldSearch': 'Recherche en champ',
  'sim.template.forestInspection': 'Inspection forêt',
  'sim.template.periurbanNight': 'Surveillance périurbaine',
  'sim.template.counterDrone': 'Défense anti-drone',

  // --- Flotte de matériels (ADR-0029) ---
  'fleet.title': 'Flotte de matériels',
  'fleet.lead':
    'Les drones, robots au sol et calculateurs sur lesquels vos modèles sont entraînés puis déployés. Chaque fiche distingue le calcul et les capteurs — ce qui adapte le modèle — des métadonnées d’airframe.',
  'fleet.add': 'Ajouter un matériel',
  'fleet.manage': 'Gérer dans Deploy →',
  'fleet.empty': 'Aucun matériel dans la flotte pour l’instant.',
  'fleet.card.componentsUnit': 'composants',
  'fleet.kind.DRONE': 'Drone',
  'fleet.kind.UGV': 'Robot au sol',
  'fleet.kind.COMPUTE': 'Calculateur',
  'fleet.kind.SENSOR': 'Capteur',
  'fleet.kind.OTHER': 'Autre',
  'fleet.status.ACTIVE': 'En service',
  'fleet.status.BENCH': 'Au banc',
  'fleet.status.MAINTENANCE': 'Maintenance',
  'fleet.status.STOCK': 'En stock',
  'fleet.family.COMPUTE': 'Calcul',
  'fleet.family.SENSOR': 'Capteur',
  'fleet.family.AIRFRAME': 'Airframe',
  'fleet.save': 'Enregistrer',
  'fleet.cancel': 'Annuler',
  'fleet.modal.createTitle': 'Ajouter un matériel',
  'fleet.modal.editTitle': 'Matériel',
  'fleet.modal.create': 'Créer le matériel',
  'fleet.field.name': 'Nom',
  'fleet.field.nameInvalid': 'Le nom est requis.',
  'fleet.field.reference': 'Référence',
  'fleet.field.kind': 'Type',
  'fleet.field.status': 'Statut',
  'fleet.field.modality': 'Modalité',
  'fleet.field.unset': '—',
  'fleet.field.family': 'Famille',
  'fleet.field.componentName': 'Composant',
  'fleet.field.componentDetail': 'Détail',
  'fleet.field.soc': 'SoC / hôte',
  'fleet.field.accelerator': 'Accélérateur',
  'fleet.field.tops': 'Budget de calcul',
  'fleet.field.latencyMs': 'Latence cible',
  'fleet.field.sensorFeed': 'Flux capteur',
  'fleet.field.inputResolution': 'Résolution d’entrée',
  'fleet.field.fovDeg': 'Champ de vue',
  'fleet.field.massG': 'Masse',
  'fleet.field.enduranceMin': 'Endurance',
  'fleet.field.propulsion': 'Motorisation',
  'fleet.field.maxSpeedMs': 'Vitesse max',
  'fleet.field.registration': 'Immatriculation',
  'fleet.field.homeBase': 'Base / stockage',
  'fleet.unit.tops': 'TOPS',
  'fleet.unit.ms': 'ms',
  'fleet.unit.deg': '°',
  'fleet.unit.g': 'g',
  'fleet.unit.min': 'min',
  'fleet.unit.ms2': 'm/s',
  'fleet.fam.model.badge': 'Adapte le modèle',
  'fleet.fam.model.title': 'Calcul & capteur',
  'fleet.fam.model.sub':
    'Le budget de calcul et les capteurs qui contraignent l’architecture, la résolution d’entrée et le format d’export du modèle.',
  'fleet.fam.meta.badge': 'Inventaire',
  'fleet.fam.meta.title': 'Airframe & métadonnées',
  'fleet.fam.meta.sub':
    'Utile à la planification terrain et au suivi de parc. N’entre jamais comme paramètre du modèle.',
  'fleet.components.title': 'Composants',
  'fleet.components.add': 'Ajouter un composant',
  'fleet.components.remove': 'Retirer',
  'fleet.wiring.note':
    'À terme, ce matériel sera la cible de déploiement d’un objectif, alimentera le déploiement et bouclera l’analyse post-déploiement.',
  'fleet.delete.trigger': 'Retirer ce matériel',
  'fleet.delete.confirm': 'Retirer définitivement ce matériel ?',
  'fleet.delete.yes': 'Retirer',

  // --- Bulles d'aide (HelpHint) ---
  'help.label': 'Aide',
  'help.overview.projects':
    "Un projet regroupe de façon transverse des vidéos, images et modèles autour d'une même finalité — indépendamment des campagnes de collecte.",
  'help.overview.fleet':
    "La flotte recense drones, robots et calculateurs. Chaque fiche distingue le calcul et les capteurs (ce qui adapte le modèle) des métadonnées d'airframe.",
  'help.overview.evaluatedModels':
    "Le mAP (mean Average Precision) mesure la qualité d'un modèle de détection : plus il est élevé, mieux le modèle localise et classe les objets.",
  'help.data.videos':
    "Importez vos vidéos de drone, puis annotez-les. L'octet transite directement vers le stockage sécurisé, jamais par un intermédiaire.",
  'help.data.images':
    "Extrayez des images fixes des vidéos pour les annoter en boîtes englobantes — la base d'un jeu d'entraînement de détection.",
  'help.data.campaigns':
    "Une campagne regroupe des sessions de vol (sorties terrain). C'est l'organisation de la collecte, distincte des projets transverses.",
  'help.data.addToProject':
    "Rattachez la sélection à un projet transverse pour la réunir avec d'autres médias et modèles autour d'une même finalité.",
  'help.model.import':
    'Importez vos propres poids ONNX. Le modèle est évalué sur vos données avant tout déploiement. Pour partir d’un modèle existant, adoptez-en un depuis le catalogue du Model Zoo.',
  'help.model.registry':
    'Vos modèles et leurs versions (haut du Model Zoo). Le mAP (qualité de détection) permet de les comparer ; seul un modèle prêt et évalué est déployable.',
  'help.model.catalog':
    'Modèles pré-entraînés (Yolo, ResNet…) prêts à adopter, puis à évaluer sur vos données. Chaque fiche indique la fiabilité, les droits d’usage et la licence.',
  'help.model.process':
    'Le chemin complet pour obtenir un modèle : définir l’objectif d’un projet (quoi détecter, sur quelles données, pour quel matériel), puis données, modèle de base, entraînement, évaluation et déploiement. L’avancement se lit sur vos données réelles.',
  'help.members.roles':
    'Les rôles vont de Lecteur (consultation) à Propriétaire, en passant par Annotateur, Éditeur et Administrateur. Chaque rôle hérite des droits des précédents.',
  'help.members.invite':
    "L'invitation envoie un lien à usage unique ; la personne rejoint l'organisation avec le rôle choisi. Une invitation en attente peut être révoquée.",
  'help.simulation.what':
    "Générez des images d'entraînement synthétiques (scènes, menaces, météo). Chaque lot est validé par un humain avant de pouvoir alimenter l'entraînement.",
  'help.objective':
    "L'objectif décrit ce que le projet cherche à détecter et dans quelles conditions. Il dérive un plan guidé, calculé sur l'état réel des données — jamais un horodatage figé.",

  // --- Visite guidée (ADR-0031) : commandes du moteur ---
  'tour.step': 'Étape',
  'tour.next': 'Suivant',
  'tour.prev': 'Précédent',
  'tour.skip': 'Passer',
  'tour.finish': 'Terminer',

  // --- Visite « overview » : présentation des quatre sections + l'aide ---
  'tour.overview.overview.title': 'Overview — votre tableau de bord',
  'tour.overview.overview.text':
    "Le point de départ : dernières vidéos, modèles évalués et état de la flotte, en un coup d'œil.",
  'tour.overview.data.title': 'Data — vos données',
  'tour.overview.data.text':
    "Déposez vos vidéos de drone et extrayez des images à annoter. L'octet va directement au stockage sécurisé, jamais par un intermédiaire.",
  'tour.overview.model.title': 'Model — vos modèles',
  'tour.overview.model.text':
    'Importez, adoptez depuis la bibliothèque ou entraînez des modèles de détection, puis suivez leurs métriques d’évaluation.',
  'tour.overview.deploy.title': 'Deploy — votre flotte',
  'tour.overview.deploy.text':
    'Recensez drones, robots et calculateurs, et composez leurs nomenclatures de pièces. Utile surtout au gestionnaire de flotte — mais toujours accessible.',
  'tour.overview.account.title': 'Votre compte et vos réglages',
  'tour.overview.account.text':
    'Profil, thème, déconnexion — et le choix de votre profil d’utilisation, que vous pourrez modifier à tout moment. Vous pourrez aussi rejouer cette visite.',

  // --- Personas (ADR-0031) : modale de première connexion + réglage profil ---
  'persona.modal.title': 'Comment utilisez-vous la plateforme ?',
  'persona.modal.subtitle':
    "Votre profil adapte votre accueil et les visites guidées proposées. Rien n'est masqué, et vous pourrez le changer à tout moment dans votre profil. C'est une préférence d'affichage — pas un rôle ni une permission.",
  'persona.modal.confirm': 'Continuer',
  'persona.modal.skip': 'Passer — rester en Généraliste',
  'persona.GENERALIST.label': 'Généraliste',
  'persona.GENERALIST.desc': 'Découverte et usage polyvalent de la plateforme.',
  'persona.GENERALIST.lands': 'Ouvre : Overview',
  'persona.COLLECTOR.label': 'Collecteur de terrain',
  'persona.COLLECTOR.desc': 'Je dépose et j’organise mes vidéos et images de vol.',
  'persona.COLLECTOR.lands': 'Ouvre : Data',
  'persona.AI_ENGINEER.label': 'Ingénieur IA',
  'persona.AI_ENGINEER.desc': 'J’entraîne, importe et évalue des modèles de détection.',
  'persona.AI_ENGINEER.lands': 'Ouvre : Model',
  'persona.FLEET_MANAGER.label': 'Gestionnaire de flotte',
  'persona.FLEET_MANAGER.desc': 'Je gère les matériels terrain et leurs nomenclatures.',
  'persona.FLEET_MANAGER.lands': 'Ouvre : Deploy',
  'persona.OPERATOR.label': 'Opérateur terrain',
  'persona.OPERATOR.desc':
    'Je défends une zone : je surveille les menaces et mes drones défenseurs.',
  'persona.OPERATOR.lands': 'Ouvre : Ops',

  // --- Vue opérationnelle counter-UAS (ADR-0040) ---
  'ops.title': 'Situation opérationnelle',
  'ops.subtitle':
    'Menaces détectées et moyens de protection sur la zone défendue, en quasi temps réel.',
  'ops.zone.label': 'Zone défendue',
  'ops.zone.loading': 'Chargement des zones…',
  'ops.zone.none': 'Aucune zone opérationnelle',
  'ops.zone.empty':
    'Aucune zone n’est encore définie. Un administrateur en crée une via l’API des opérations.',
  'ops.picture.loading': 'Chargement de la situation…',
  'ops.picture.error': 'La situation n’a pas pu être chargée.',
  'ops.live': 'En direct',
  'ops.updated': 'Mise à jour',
  'ops.basemap.missing':
    'Fond cartographique non configuré : positions affichées sur fond neutre (souverain).',
  'ops.threats.title': 'Menaces',
  'ops.threats.none': 'Aucune menace active',
  'ops.defenders.title': 'Défenseurs',
  'ops.defenders.none': 'Aucun défenseur positionné',
  'ops.legend.title': 'Légende',
  'ops.field.speed': 'Vitesse',
  'ops.field.heading': 'Cap',
  'ops.field.altitude': 'Altitude',
  'ops.field.size': 'Taille',
  'ops.field.battery': 'Batterie',
  'ops.field.lastSeen': 'Vue',
  'ops.level.LOW': 'Faible',
  'ops.level.MEDIUM': 'Moyen',
  'ops.level.HIGH': 'Élevé',
  'ops.level.CRITICAL': 'Critique',
  'ops.class.UNKNOWN': 'Inconnu',
  'ops.class.MULTIROTOR': 'Multirotor',
  'ops.class.FIXED_WING': 'Voilure fixe',
  'ops.class.HELICOPTER': 'Hélicoptère',
  'ops.class.OTHER': 'Autre',
  'ops.status.AVAILABLE': 'Disponible',
  'ops.status.ENGAGED': 'Engagé',
  'ops.status.RETURNING': 'Retour',
  'ops.status.CHARGING': 'En charge',
  'ops.status.OFFLINE': 'Hors ligne',
  'ops.manage.add': 'Rattacher',
  'ops.manage.assetLabel': 'Matériel à rattacher',
  'ops.manage.noCandidates': 'Aucun drone/UGV disponible à rattacher.',
  'ops.manage.status': 'État du défenseur',
  'ops.manage.reposition': 'Repositionner',
  'ops.manage.remove': 'Retirer',
  'ops.manage.placing': 'Cliquez sur la carte pour positionner',
  'ops.manage.cancelPlacing': 'Annuler',
  'ops.manage.error': 'L’action a échoué.',
  'ops.zone.new': 'Nouvelle zone',
  'ops.zone.create.title': 'Créer une zone défendue',
  'ops.zone.create.name': 'Nom de la zone',
  'ops.zone.create.lat': 'Latitude du centre',
  'ops.zone.create.lon': 'Longitude du centre',
  'ops.zone.create.radius': 'Rayon défendu (m)',
  'ops.zone.create.submit': 'Créer la zone',
  'ops.zone.create.cancel': 'Annuler',
  'ops.sim.label': 'Menaces simulées',
  'ops.sim.on': 'Activées',
  'ops.sim.off': 'Désactivées',

  // --- Visites de section (ADR-0031, incrément 2) : rejeu + contenu par page ---
  'tour.replay': 'Revoir la visite',
  'tour.data.videos.title': 'Vos vidéos',
  'tour.data.videos.text':
    'Importez vos vidéos de drone et suivez leur statut. Depuis une vidéo prête, vous pouvez annoter et extraire des images.',
  'tour.data.images.title': 'Images extraites',
  'tour.data.images.text':
    'Les images fixes tirées des vidéos se rassemblent ici : la matière à annoter en boîtes englobantes pour l’entraînement.',
  'tour.model.import.title': 'Importer ou adopter un modèle',
  'tour.model.import.text':
    'Partez d’un modèle de la bibliothèque organisée, ou importez vos propres poids ONNX pour les évaluer sur vos données.',
  'tour.model.registry.title': 'Registre des modèles',
  'tour.model.registry.text':
    'Vos modèles et leurs versions, avec leurs métriques d’évaluation (mAP) — de quoi comparer et choisir lequel déployer.',
  'tour.deploy.compute.title': 'Calcul embarqué',
  'tour.deploy.compute.text':
    'Le calculateur et les capteurs d’un matériel : ce qui détermine quel modèle peut y tourner, et comment il sera adapté.',
  'tour.deploy.hardware.title': 'Hardware — catalogue & nomenclature',
  'tour.deploy.hardware.text':
    'Consultez le catalogue de pièces organisé et composez la nomenclature de chaque matériel de la flotte.',

  // --- Hardware : catalogue de pièces + nomenclature (ADR-0030, incrément 1c) ---
  // --- Packaging de modèle : déploiement edge (ADR-0034) ---
  'deploy.packaging.title': 'Packaging',
  'deploy.packaging.lead':
    'Empaquetez un modèle validé pour le runtime cible d’un matériel (ONNX, TFLite, TensorRT), avec la quantification adaptée à son budget de calcul.',
  'deploy.packaging.dormantNote':
    'Écrire un paquet est possible ; la construction est dormante tant que le nœud de conversion n’est pas activé.',
  'deploy.packaging.dormantTitle': 'Construction indisponible',
  'deploy.packaging.dormantBody':
    'Le packaging est dormant : la conversion/quantification se fera sur un nœud externe, activé ultérieurement.',
  'help.deploy.packaging':
    'Un paquet fige les choix de packaging (runtime, quantification, taille d’entrée) d’un modèle validé pour une cible matérielle. C’est l’enveloppe calcul de la cible — pas son airframe — qui recommande le format et la quantification.',
  'deploy.packaging.field.model': 'Modèle validé',
  'deploy.packaging.field.modelPlaceholder': 'Choisir un modèle…',
  'deploy.packaging.field.target': 'Cible matérielle',
  'deploy.packaging.field.targetNone': 'Aucune cible (générique)',
  'deploy.packaging.field.runtime': 'Runtime',
  'deploy.packaging.field.quantization': 'Quantification',
  'deploy.packaging.field.inputSize': 'Taille d’entrée',
  'deploy.packaging.noReadyModel': 'Aucun modèle validé (READY) à empaqueter pour l’instant.',
  'deploy.packaging.computeEnvelope': 'Enveloppe calcul de la cible',
  'deploy.packaging.computeEmpty': 'Enveloppe calcul non renseignée',
  'deploy.packaging.recommendation': 'Recommandé',
  'deploy.packaging.create': 'Créer le paquet',
  'deploy.packaging.createError': 'Création impossible.',
  'deploy.packaging.buildError': 'Lancement impossible.',
  'deploy.packaging.downloadError': 'Téléchargement impossible.',
  'deploy.packaging.packages': 'Paquets',
  'deploy.packaging.empty': 'Aucun paquet pour l’instant.',
  'deploy.packaging.build': 'Construire',
  'deploy.packaging.download': 'Télécharger le binaire',
  'deploy.packaging.delete': 'Supprimer',
  'deploy.packaging.runtime.onnx': 'ONNX',
  'deploy.packaging.runtime.tflite': 'TFLite',
  'deploy.packaging.runtime.tensorrt': 'TensorRT',
  'deploy.packaging.quant.none': 'Aucune (FP32)',
  'deploy.packaging.quant.fp16': 'FP16',
  'deploy.packaging.quant.int8': 'INT8',
  'deploy.packaging.status.draft': 'Brouillon',
  'deploy.packaging.status.queued': 'En file',
  'deploy.packaging.status.building': 'Construction',
  'deploy.packaging.status.ready': 'Prêt',
  'deploy.packaging.status.failed': 'Échec',
  'deploy.hardwareLabel': 'Hardware',
  'deploy.hardwareText':
    'Le matériel embarqué : composez la nomenclature de chaque appareil à partir du catalogue de pièces organisé.',
  'help.deploy.hardware':
    'Le catalogue recense les pièces FPV de référence (châssis, moteurs, ESC, caméras…). La nomenclature liste les pièces d’un matériel donné, chacune renvoyant à une référence officielle du catalogue.',
  'hw.tab.catalog': 'Catalogue',
  'hw.tab.build': 'Nomenclature',
  'hw.catalog.searchLabel': 'Rechercher une pièce',
  'hw.catalog.searchPlaceholder': 'marque, modèle…',
  'hw.catalog.all': 'Toutes',
  'hw.catalog.curated':
    'Catalogue global organisé : consultable par tous ; l’ajout et la dépréciation de références sont réservés au rôle SUPERADMIN. Zéro doublon (identité unique catégorie·marque·modèle·variante).',
  'hw.catalog.empty': 'Aucune référence pour ce filtre.',
  'hw.photos.title': 'Photos du build',
  'hw.photos.add': 'Ajouter des photos',
  'hw.photos.uploading': 'Envoi…',
  'hw.photos.hint':
    'Depuis votre navigateur — JPEG, PNG ou WebP, 8 Mo max. L’octet ne passe pas par l’API.',
  'hw.photos.remove': 'Retirer',
  'hw.photos.badType': 'Format non supporté (JPEG, PNG ou WebP).',
  'hw.photos.tooBig': 'Photo trop volumineuse (max 8 Mo).',
  'hw.photos.uploadFailed': 'Échec de l’envoi.',
  'hw.media.edit': 'Modifier l’image',
  'hw.media.add': 'Ajouter une image',
  'hw.media.title': 'Illustration de la pièce',
  'hw.media.close': 'Fermer',
  'hw.media.importLabel': 'Importer depuis l’URL fabricant',
  'hw.media.importPlaceholder': 'https://fabricant.com/…/produit.jpg',
  'hw.media.import': 'Importer',
  'hw.media.or': 'ou',
  'hw.media.upload': 'Téléverser un fichier',
  'hw.media.remove': 'Retirer l’image',
  'hw.media.hint': 'Hôtes fabricants autorisés uniquement. JPEG/PNG/WebP, 5 Mo max.',
  'hw.media.working': 'Traitement…',
  'hw.media.needUrl': 'Collez une URL fabricant.',
  'hw.media.badType': 'Format non supporté (JPEG, PNG ou WebP).',
  'hw.media.tooBig': 'Image trop volumineuse (max 5 Mo).',
  'hw.media.uploadFailed': 'Échec de l’envoi.',
  'hw.status.active': 'Actif',
  'hw.status.deprecated': 'Déprécié',
  'hw.cat.FRAME': 'Châssis',
  'hw.cat.MOTOR': 'Moteurs',
  'hw.cat.ESC': 'ESC',
  'hw.cat.FC': 'Contrôleur de vol',
  'hw.cat.CAMERA': 'Caméras',
  'hw.cat.VTX': 'Émetteur vidéo',
  'hw.cat.RX': 'Récepteur',
  'hw.cat.BATTERY': 'Batterie',
  'hw.cat.PROP': 'Hélices',
  'hw.cat.ANTENNA': 'Antennes',
  'hw.cat.GPS': 'GPS',
  'hw.cat.OTHER': 'Autre',
  'hw.build.assetLabel': 'Matériel',
  'hw.build.addPart': 'Ajouter une pièce',
  'hw.build.save': 'Enregistrer',
  'hw.build.saved': 'Nomenclature enregistrée',
  'hw.build.saveError': 'Enregistrement impossible.',
  'hw.build.empty': 'Aucune pièce. « Ajouter une pièce » pour composer la nomenclature.',
  'hw.build.note':
    'Chaque ligne référence une pièce officielle du catalogue. Les variantes tunées d’organisation (rattachées à un officiel) arrivent dans un prochain incrément.',
  'hw.build.summary': 'Récapitulatif',
  'hw.build.lines': 'Lignes',
  'hw.build.totalQty': 'Pièces (qté totale)',
  'hw.build.coverage': 'Couverture par catégorie',
  'hw.build.col.slot': 'Emplacement',
  'hw.build.col.part': 'Pièce',
  'hw.build.col.qty': 'Qté',
  'hw.build.qtyMinus': 'Diminuer la quantité',
  'hw.build.qtyPlus': 'Augmenter la quantité',
  'hw.build.remove': 'Retirer la ligne',
  'hw.build.pickHeading': 'Ajouter une pièce',
  'hw.build.pickAdd': 'Ajouter',
  'hw.build.noAsset.title': 'Aucun matériel dans la flotte',
  'hw.build.noAsset.body': 'Ajoutez d’abord un matériel à la flotte pour composer sa nomenclature.',
  'hw.build.noAsset.link': 'Aller à la flotte',

  // --- Variantes tunées de nomenclature (ADR-0030 §3, incrément 1d) ---
  'hw.build.variantLabel': 'Variante',
  'hw.build.official': '— (officiel)',
  'hw.build.newVariant': '+ Créer une variante…',
  'hw.build.tuned': 'tunée',
  'hw.build.tunedCount': 'Variantes tunées',
  'hw.variant.heading': 'Créer une variante tunée',
  'hw.variant.for': 'Dérive de',
  'hw.variant.labelField': 'Nom de la variante',
  'hw.variant.labelPlaceholder': 'ex. Rebobinage 1750KV',
  'hw.variant.noteField': 'Note (facultatif)',
  'hw.variant.overrides': 'Surcharges de specs (facultatif)',
  'hw.variant.overrideKey': 'clé',
  'hw.variant.overrideValue': 'valeur',
  'hw.variant.addOverride': 'Ajouter une surcharge',
  'hw.variant.removeOverride': 'Retirer la surcharge',
  'hw.variant.create': 'Créer',
  'hw.variant.creating': 'Création…',
  'hw.variant.cancel': 'Annuler',
  'hw.variant.createError': 'Création impossible.',

  'search.open': 'Recherche avancée',
  'search.back': '← Vidéos',
  'search.title': 'Recherche avancée',
  'search.subtitle':
    'Filtrez les vidéos par texte, appareil, pilote, dates, annotations et labels.',
  'search.query': 'Recherche plein texte',
  'search.queryPlaceholder': 'ex. ligne HTA pylône',
  'search.status': 'Statut',
  'search.any': 'Tous',
  'search.asset': 'Appareil',
  'search.anyAsset': 'Tous les appareils',
  'search.pilot': 'Pilote',
  'search.flownFrom': 'Vol depuis le',
  'search.flownTo': 'Vol jusqu’au',
  'search.annotations': 'Annotations',
  'search.withAnnotations': 'Avec annotations',
  'search.withoutAnnotations': 'Sans annotations',
  'search.sortBy': 'Trier par',
  'search.sort.createdAt': 'Date d’ajout',
  'search.sort.flownAt': 'Date de captation',
  'search.sort.title': 'Titre',
  'search.sort.duration': 'Durée',
  'search.order': 'Ordre',
  'search.order.desc': 'Décroissant',
  'search.order.asc': 'Croissant',
  'search.labels': 'Labels',
  'search.submit': 'Rechercher',
  'search.reset': 'Réinitialiser',
  'search.errorTitle': 'Recherche impossible',
  'search.noResults': 'Aucune vidéo ne correspond à ces critères.',
  'search.results': 'résultat(s)',
  'search.loadMore': 'Charger plus',
  'search.loading': 'Chargement…',
  'search.geo.title': 'Emprise géographique',
  'search.geo.canvasLabel': 'Carte de sélection d’emprise ; dessinez un rectangle pour filtrer.',
  'search.geo.hint': 'Dessinez un rectangle sur la carte pour restreindre à une zone.',
  'search.geo.emptyHint':
    'Aucune trace de vol géolocalisée pour l’instant — dessinez tout de même une zone.',
  'search.geo.clear': 'Effacer l’emprise',
} as const;
